import { yupResolver } from '@hookform/resolvers/yup'
import { Button, TextInput } from 'components/common'
import Modal from 'components/modal'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { createPickerMappingApi } from 'utils/api/pickerEmpMapApi'
import { handleError } from 'utils/helpers/handleError'
import { addPickerMapSchema } from 'utils/schema/pickerEmpMapSchema'

export interface PickerEmpFormData {
  pickerName: string
  empId: string
  pickerId: string
}

type Props = {
  isOpen: boolean
  closeModal: () => void
  onSuccess: (data: PickerEmpFormData) => Promise<void>
}

const CreatePickerMapModal = ({ isOpen, closeModal, onSuccess }: Props) => {
  const [loading, setLoading] = useState<boolean>(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PickerEmpFormData>({
    resolver: yupResolver(addPickerMapSchema),
  })

  const handleMappingCreation = async (data: PickerEmpFormData) => {
    const toastId = toast.loading('Creating picker employee mapping...')
    setLoading(true)
    try {
      await createPickerMappingApi(data)
      await onSuccess(data)

      toast.success('Picker employee mapping created successfully', {
        id: toastId,
      })
      reset()
      closeModal()
    } catch (error) {
      handleError(error, toastId, {
        default: 'Failed to create picker employee mapping',
        PICKER_NAME_ALREADY_EXIST: 'Picker name already exists',
        PICKER_ID_NOT_FOUND: 'Picker ID not found in Order Tracker',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      width={500}
      title={`Create Picker Employee Mapping`}
      titleClassname="text-xl font-semibold"
      isOpen={isOpen}
      closeModal={closeModal}
      customStyle={{
        padding: '25px 30px',
      }}
    >
      <form
        className="flex flex-col gap-2"
        onSubmit={handleSubmit(handleMappingCreation)}
      >
        <TextInput
          register={register}
          name="pickerName"
          label="Picker Name"
          error={errors?.pickerName?.message}
        />

        <TextInput
          register={register}
          name="empId"
          label="Employee Id"
          error={errors?.empId?.message}
        />

        <TextInput
          register={register}
          name="pickerId"
          label="Picker Id (Order Tracker)"
          error={errors?.pickerId?.message}
        />

        {/* Actions */}
        <div className="mt-7 flex w-full max-w-xs items-center gap-2">
          <Button
            disabled={loading}
            type="submit"
            className="w-full"
            buttonText="Confirm"
          />
          <Button
            className="w-full"
            buttonText="Cancel"
            variant="outline"
            type="button"
            onClick={closeModal}
          />
        </div>
      </form>
    </Modal>
  )
}

export default CreatePickerMapModal
